import * as moment from 'moment';

import { generateGuid, isValidUUID } from '../util/stringHelper';

import { FieldType, IConditionButton, IConditionFilter, IConditionPayload, IConditionSuggestion, IConditionQueryField } from './model';

import type { IConditionFilterDatalabel, IConditionFilterHide, IConditionFilterShow } from '../../../entities/src/model';
import type { ISegmentConditionFilter, ISegmentCriteria } from '../../../lists/src/model';
import * as R from 'ramda';
import { dateFunctionOptions, daysTransform, monthsTransform, weekTransform } from './strings';
import { format } from 'date-fns';

export const getTransformedComparers = (isPassed: boolean) => [
	{
		label: 'is',
		value: 'Is',
	},
	{
		label: 'is not',
		value: 'IsNot',
	},
	{
		label: !isPassed ? 'is before' : 'is less than',
		value: 'IsLessThan',
	},
	{
		label: !isPassed ? 'is before or on' : 'is at most',
		value: 'IsLessThanOrEqualTo',
	},
	{
		label: !isPassed ? 'is after' : 'is more than',
		value: 'IsGreaterThan',
	},
	{
		label: !isPassed ? 'is after or on' : 'is at least',
		value: 'IsGreaterThanOrEqualTo',
	},
	{
		label: 'is between',
		value: 'IsBetween',
	},
	{
		label: 'is not between',
		value: 'IsNotBetween',
	},
];

export const isBetweenComparer = (comparer: string) => {
	return comparer?.toLowerCase().includes('between');
};

export const getCriteriaOptions = (conditionQueryFields: IConditionQueryField[]) => {
	if (conditionQueryFields && conditionQueryFields.length) {
		return conditionQueryFields.map((field) => {
			return {
				label: field.Name,
				value: field.Key,
				isDisabled: field.IsDisabled,
			};
		});
	}

	return [];
};

export const getComparerOptions = (field: IConditionQueryField) => {
	const comparer = field.Comparers;

	const comparers = [];

	for (const key in comparer) {
		if (comparer.hasOwnProperty(key)) {
			if ((field.Type === 'Decimal' || field.Type === 'Integer') && isBetweenComparer(key)) {
				continue;
			}
			comparers.push({
				label: comparer[key],
				value: key,
			});
		}
	}

	return comparers;
};

export const constructConditionFilter = (
	field: IConditionQueryField,
	filterLabel: IConditionFilterDatalabel,
	constructConditionButtons: (extraOption: boolean, filterLabel: IConditionFilterDatalabel) => IConditionButton[],
	id?: string
): IConditionFilter => {
	const comparers = getComparerOptions(field);

	return {
		comparer: comparers[0].value,
		comparerOptions: comparers,
		criteria: field.Key,
		criteriaType: field.CriteriaType,
		dateBetween: {
			EndDate: moment().add(1, 'day').format(),
			StartDate: moment().format(),
		},
		extraOption: '',
		id: id ? id : generateGuid(),
		interval: {
			type: 1,
			value: 5,
		},
		buttons: constructConditionButtons(field.IsDateRangeField, filterLabel),
		suggestions: field.Options ? field.Options : [],
		value: getDefaultConditionFilterValue(field.Type, comparers[0].value),
		valueType: field.Type,
	};
};

export const constructConditionFilterForSegment = (
	field: IConditionQueryField,
	filterLabel: IConditionFilterDatalabel,
	constructConditionButtons: (extraOption: boolean, filterLabel: IConditionFilterDatalabel) => IConditionButton[],
	id?: string
) => {
	const comparers = getComparerOptions(field);
	let fieldOptions = {
		IsTimesFilterSupported: field.IsTimesFilterSupported,
		IsCrossMailingListField: field.IsCrossMailingListField,
		IsProductFilterSupported: field.IsProductFilterSupported,
		IsDateFunctionSupported: field.IsDateFunctionSupported,
		IsWebsiteFilterSupported: field.IsWebsiteFilterSupported,
		IsBrandFilterSupported: field.IsBrandFilterSupported,
		IsCategoryFilterSupported: field.IsCategoryFilterSupported,
	};
	fieldOptions = field.MinAllowedDate ? { ...fieldOptions, MinAllowedDate: field.MinAllowedDate } : fieldOptions;
	const criteriaItem: ISegmentConditionFilter = {
		comparer: comparers.length ? comparers[0].value : '',
		comparerOptions: comparers,
		criteria: field.Key,
		// criteriaType: field.CriteriaType,
		extraOption: '',
		id: id ? id : generateGuid(),
		dateBetween: {
			EndDate: format(new Date(), 'yyyy-MM-dd'),
			StartDate: format(new Date(), 'yyyy-MM-dd'),
		},
		interval: {
			type: 1,
			value: 5,
		},
		buttons: constructConditionButtons(field.IsDateRangeField, filterLabel),
		suggestions: field.Options ? field.Options : [],
		value: comparers.length ? getDefaultConditionFilterValue(field.Type, comparers[0].value) : '',
		valueType: field.Type,
		fieldOptions,
		isCustomValue: false,
	};

	return criteriaItem;
};

export const getDefaultConditionFilterValue = (type: FieldType, comparer: string) => {
	switch (type) {
		case 'Boolean':
			return '';

		case 'Text':
			return '';

		case 'Guid':
			return '';

		case 'Integer':
			return '';

		case 'Decimal':
			return '';
		case 'DateTime':
		case 'DateTimeWithPrecision':
			return getDefaultConditionFilterDate(comparer);
		case 'Date':
			return getDefaultConditionFilterDate(comparer);
		case 'Enum':
			return '';
		default:
			return;
	}
};

export const getDefaultConditionFilterDate = (comparer: string) => {
	if (isBetweenComparer(comparer)) {
		// in default format for the backend
		return moment().format('YYYY-MM-DD') + '|' + moment().add(1, 'day').format('YYYY-MM-DD');
	}
	// return moment().add(1, 'day').format();
	return format(new Date(), 'yyyy-MM-dd');
};

export const createConditionPayload = (conditionFilters: IConditionFilter[], entityId?: string) => {
	const returnArray: IConditionPayload[] = [];

	conditionFilters.forEach((item) => {
		const payload: IConditionPayload = {
			comparer: item.comparer,
			criteriaType: item.criteriaType,
			dateBetween: item.extraOption === 'dateBetween' ? item.dateBetween : null,
			entityId,
			frequencyType: item.extraOption === 'interval' ? item.interval.type : 0,
			id: item.id,
			lastXMinutes: item.extraOption === 'interval' ? item.interval.value : null,
			queryField: item.criteria,
			queryFieldValue: item.value,
		};

		returnArray.push(payload);
	});

	return returnArray;
};

export const constructConditionFilterFromData = (conditionFilters: IConditionPayload[]) => {
	const returnArray: IConditionFilter[] = [];

	conditionFilters.forEach((item) => {
		const condition: IConditionFilter = {
			comparer: item.comparer,
			comparerOptions: [],
			criteria: item.queryField,
			criteriaType: item.criteriaType,
			dateBetween: item.dateBetween
				? item.dateBetween
				: {
						EndDate: moment().add(1, 'day').format(),
						StartDate: moment().format(),
				  },
			extraOption: item.dateBetween ? 'dateBetween' : item.lastXMinutes ? 'interval' : '',
			id: item.id,
			interval: {
				type: item.frequencyType ? item.frequencyType : 1,
				value: item.lastXMinutes ? item.lastXMinutes : 5,
			},
			suggestions: [],
			value: Array.isArray(item.queryFieldValue) ? item.queryFieldValue.join('|') : item.queryFieldValue,
			valueType: 'Boolean',
		};

		returnArray.push(condition);
	});

	return returnArray;
};

export const reconstructMissingData = (
	conditionFilter: IConditionFilter,
	field: IConditionQueryField,
	filterLabel: IConditionFilterDatalabel,
	constructConditionButtons: (extraOption: boolean, filterLabel: IConditionFilterDatalabel) => IConditionButton[],
	getSegmentsSuggestions: (field: string, filterLabel: IConditionFilterDatalabel, id: string, criteriaType: number) => void
) => {
	const valueType: FieldType = conditionFilter.valueType || field.Type;
	const newConditionFilter: IConditionFilter = {
		...conditionFilter,
		comparerOptions: !conditionFilter.comparerOptions?.length ? getComparerOptions(field) : conditionFilter.comparerOptions,
		buttons: constructConditionButtons(field.IsDateRangeField, filterLabel),
		suggestions: field.Options || [],
		valueType: !conditionFilter.comparerOptions?.length ? valueType : conditionFilter.valueType,
	};

	if (!conditionFilter.suggestions?.length) {
		getSegmentsSuggestions(conditionFilter.criteria, filterLabel, conditionFilter.id, conditionFilter.criteriaType);
	}

	return newConditionFilter;
};

export const selectEntitiesCondition = (
	conditionFilterForm: IConditionFilterHide | IConditionFilterShow,
	dataLabel: IConditionFilterDatalabel,
	id: string,
	suggestions?: IConditionSuggestion[]
) => {
	const conditionFilters: IConditionFilter[] = (conditionFilterForm as any)[dataLabel.filters];

	const index =
		conditionFilters &&
		conditionFilters.findIndex((item) => {
			return item.id === id;
		});

	const newSuggestions = suggestions ? { suggestions } : {};

	const newCondition = {
		...conditionFilters[index],
		...newSuggestions,
		loading: false,
	};

	conditionFilters.splice(index, 1, newCondition);
	return conditionFilters;
};

// Convert minutes to days or hours if reached
const getLastXMinutesType = (value: number) => {
	if (value % 1440 === 0) {
		return { type: 3, value: value / 1440 };
	} else if (value % 60 === 0) {
		return { type: 2, value: value / 60 };
	} else {
		return { type: 1, value };
	}
};

const typesInMinutes = { 1: 1, 2: 60, 3: 1440 };

const convertLastXMinutes = (type: number, value: number) => {
	return value * typesInMinutes[type as keyof typeof typesInMinutes];
};

export const transformCriteriasToPayload = (elements: ISegmentConditionFilter[]) => {
	return (
		(elements.length &&
			elements.map((item) => {
				const criteriaTransformed: ISegmentCriteria = {
					Field: isValidUUID(item.criteria) ? 'CustomField' : item.criteria,
					Comparer: item.comparer,
					Value: item.value,
				};

				if (isValidUUID(item.criteria)) {
					criteriaTransformed.CustomFieldId = item.criteria;
					criteriaTransformed.CustomField = item.customField;
				}
				if (item.extraOption === 'dateBetween' && item.dateBetween) {
					criteriaTransformed.DateFrom = item.dateBetween.StartDate || '';
					criteriaTransformed.DateTo = item.dateBetween.EndDate;
				}
				if (item.extraOption === 'interval')
					criteriaTransformed.LastXMinutes = convertLastXMinutes(item.interval.type, item.interval.value);
				if (item.productCode && item.productCode !== 'any') criteriaTransformed.ProductCode = item.productCode;
				if (item.timesFilter) criteriaTransformed.Times = item.timesFilter;
				if (item.website && item.website !== 'any') criteriaTransformed.WebsiteId = item.website;
				if (item.dateFunction && item.dateFunction !== 'NoTransform') criteriaTransformed.DateFunction = item.dateFunction;
				if (item.productCategory && item.productCategory !== 'any') criteriaTransformed.ProductCategory = item.productCategory;
				if (item.productBrand && item.productBrand !== 'any') criteriaTransformed.ProductBrand = item.productBrand;
				return criteriaTransformed;
			})) ||
		[]
	);
};

export const transformCriteriasToUI = (criterias: ISegmentCriteria[], fields: IConditionQueryField[]) => {
	return criterias.map((criteria) => {
		const fieldName = criteria.CustomFieldId ? criteria.CustomFieldId : criteria.Field;
		const selectedField = fields.find((item) => item.Key === fieldName);
		if (!selectedField) {
			return;
		}

		let dateBetween = {
			EndDate: moment().add(1, 'day').format(),
			StartDate: moment().format(),
		};

		if (criteria.DateFrom && criteria.DateTo) {
			dateBetween = {
				EndDate: criteria.DateTo,
				StartDate: criteria.DateFrom,
			};
		}

		const criteriaTransformed: ISegmentConditionFilter = {
			criteria: criteria.CustomFieldId ? criteria.CustomFieldId : criteria.Field,
			customField: criteria.CustomField,
			comparer: criteria.Comparer,
			value: criteria.Value,
			extraOption: criteria.DateFrom ? 'dateBetween' : criteria.LastXMinutes ? 'interval' : '',
			comparerOptions: [],
			suggestions: [],
			valueType: selectedField.Type,
			id: criteria.Id ? criteria.Id?.toString() : generateGuid(),
			dateBetween,
			interval: {
				type: 1,
				value: 5,
			},
			isOldDate: selectedField.Type === 'Date',
		};

		if (criteria.ProductCode) criteriaTransformed.productCode = criteria.ProductCode;
		if (criteria.ProductCategory) criteriaTransformed.productCategory = criteria.ProductCategory;
		if (criteria.ProductBrand) criteriaTransformed.productBrand = criteria.ProductBrand;
		if (criteria.Times) criteriaTransformed.timesFilter = criteria.Times;
		if (criteria.WebsiteId) criteriaTransformed.website = criteria.WebsiteId.replace(/-/g, '');
		if (criteria.DateFunction) {
			criteriaTransformed.dateFunction = criteria.DateFunction;
			criteriaTransformed.valueType = 'Text';
		}
		if (criteria.LastXMinutes)
			criteriaTransformed.interval = {
				type: getLastXMinutesType(criteria.LastXMinutes).type,
				value: getLastXMinutesType(criteria.LastXMinutes).value,
			};

		let fieldOptions = {
			IsTimesFilterSupported: selectedField.IsTimesFilterSupported,
			IsCrossMailingListField: selectedField.IsCrossMailingListField,
			IsProductFilterSupported: selectedField.IsProductFilterSupported,
			IsDateFunctionSupported: selectedField.IsDateFunctionSupported,
			IsWebsiteFilterSupported: selectedField.IsWebsiteFilterSupported,
			IsDisabled: selectedField.IsDisabled,
			IsBrandFilterSupported: selectedField.IsBrandFilterSupported,
			IsCategoryFilterSupported: selectedField.IsCategoryFilterSupported,
		};
		fieldOptions = selectedField.MinAllowedDate ? { ...selectedField, MinAllowedDate: selectedField.MinAllowedDate } : selectedField;

		if (R.find(R.propEq('value', criteria.DateFunction))(dateFunctionOptions)) {
			const isPassed = criteria.DateFunction?.includes('Passed');
			criteriaTransformed.comparerOptions = getTransformedComparers(!!isPassed);
		}

		criteriaTransformed.fieldOptions = fieldOptions;

		return criteriaTransformed;
	});
};

export const getDateTransformOptions = (type: string) => {
	switch (type) {
		case 'Month':
			return monthsTransform;
		case 'Day':
			return daysTransform;
		case 'WeekDay':
			return weekTransform;
		default:
			return undefined;
	}
};

export const isSpecialDateTransform = (type: string) => ['Month', 'Day', 'WeekDay'].includes(type);
